import { useMutation, useQuery } from '@apollo/client';
import {
  ContentDeciderContainer,
  PageLoading,
  toast,
  AppContext,
  isAuthorised,
  SkeletonLoading,
  ALL_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  getCurrentUserHook,
  useIsAuthorisedHook,
  EventEmitter,
  useProduct,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  GET_TICKET,
  UPDATE_TICKET,
  GET_FIELD,
  useCachedQuery,
  AccountClient,
  savePersonalization,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import { TicketDetailsPage } from '@nile/mangifera/src/Pages/TicketDetails';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import envConfig from '@nile/home/src/Config/env';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import ErrorPage from '@nile/components/src/Components/ErrorPage';
import EmptyContainer from '@nile/components/src/Containers/EmptyContainer';
import { isEmpty } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getTicketUpdates } from '@nile/mangifera/src/Utils/Hooks';
import InvoiceItemsHook from '@nile/components/src/Components/InvoiceItemsHook';

const detailsRef = React.createRef();

function MainContainer(props) {
  const { id, isClassicView } = props;
  const flags = useFlags();

  const [ticket, setTicket] = useState({});
  const context = useContext(TicketContext);
  const appContext = useContext(AppContext);
  const { productsToInvoice, getItemsToInvoice } = InvoiceItemsHook();

  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const user = getCurrentUserHook();
  const {
    loading: priorityLoading,
    data: priorityValues,
    error: priorityError
  } = useQuery(GET_FIELD, {
    client: AccountClient,
    skip: !useIsAuthorisedHook('customFields.view'),
    variables: {
      input: {
        module: ALL_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.PRIORITY
      }
    }
  });
  const { loading, error, data, refetch } = useCachedQuery(GET_TICKET, {
    variables: { id, isMSPProduct }
  });

  const {
    loading: personalisationLoading,
    error: personalisationError,
    data: personalisationData
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    fetchPolicy: 'network-only',
    client: AccountClient,
    variables: {
      userId: user?.userId,
      category: 'ticketDetailFormState',
      name: user?.userId
    }
  });

  const formDataFromPersonalisation = !isEmpty(
    personalisationData?.getPersonalizationByCategoryAndName?.value
  )
    ? JSON.parse(
        personalisationData?.getPersonalizationByCategoryAndName?.value
      )
    : null;

  useEffect(() => {
    if (data?.getTicket) {
      const { getTicket } = data;
      setTicket(getTicket);
      if (detailsRef.current)
        detailsRef.current.updateNewTicketProperties(getTicket);

      if (!context?.activeTab?.data?.subject && !isClassicView) {
        context.updateTab({
          module: 'TICKET',
          data: {
            ticketId: getTicket.ticketId,
            subject: getTicket.subject,
            displayId: getTicket.displayId
          }
        });
      }
    }
  }, [data]);
  const options = { pageSize: 100, page: 1 };

  const [updateTicket] = useMutation(UPDATE_TICKET, {
    onCompleted({ saveTicket }) {
      /* Close sheet container once an employee created */
      if (saveTicket) {
        toast('success', 'New ticket created successfully');
        window.setTimeout(() => {
          context.closeTab(context.activeTab);
        }, 400);
      }
    },
    onError() {
      // toast('error', 'Something went wrong');
    }
  });

  const refetchTicket = useCallback(() => {
    return refetch().then((res) => {
      if (res?.data?.getTicket) {
        setTicket(res?.data?.getTicket);
        detailsRef.current.updateNewTicketProperties(res.data.getTicket);
      }
      return res?.data?.getTicket;
    });
  }, []);

  const updateTicketState = (newTicketData) => {
    setTicket(newTicketData);
  };

  const back = useCallback(() => {
    // props.history.push('/onboard/customfields');
  }, []); // eslint-disable-line

  const saveFormStatesToPersonalisation = (formData) => {
    const personalisationValue = {
      ticketDetailFormState: formData
    };

    const personalisedData = {
      user: {
        firstName: user?.firstName,
        lastName: user?.lastName,
        userId: user?.userId,
        email: user?.email
      },
      category: 'ticketDetailFormState',
      name: user?.userId,
      value: JSON.stringify(personalisationValue)
    };
    savePersonalization({
      variables: personalisedData,
      input: personalisedData
    });
  };

  const { sendClientEvent, concurrentViewers } = getTicketUpdates(
    ticket?.ticketId
  );

  useEffect(() => {
    EventEmitter.subscribe('currentTicketDeleted', () => {
      setTicket((prevState) => {
        return {
          ...prevState,
          dormantType: 'DELETED',
          operationalStatus: 'DORMANT'
        };
      });
    });
    EventEmitter.subscribe('currentTicketSpammed', () => {
      setTicket((prevState) => {
        return { ...prevState, dormantType: 'SPAM' };
      });
    });
    EventEmitter.subscribe('currentTicketRestored', () => {
      setTicket((prevState) => {
        return { ...prevState, operationalStatus: 'ACTIVE' };
      });
    });
    EventEmitter.subscribe('updateClassicDetails', (updatedTicket) => {
      setTicket(updatedTicket);
    });
    EventEmitter.subscribe('refetchTicketFromAI', refetchTicket);

    return function cleanup() {
      EventEmitter.unsubscribe('currentTicketDeleted');
      EventEmitter.unsubscribe('currentTicketRestored');
      EventEmitter.unsubscribe('updateClassicDetails');
      EventEmitter.unsubscribe('refetchTicketFromAI');
      EventEmitter.unsubscribe('currentTicketSpammed');
    };
  }, []);

  const actions = {
    primaryAction: updateTicket,
    back: back,
    updateParent: updateTicketState,
    ...props.actions
  };

  if (error && error?.message !== 'Observable cancelled prematurely') {
    return (
      <ErrorPage
        imageURL={`url(${envConfig.imageRoot}svg/PageNotFound.svg)`}
        text="Not Found"
        description="Built for a great technician experience and a greater IT service delivery experience"
      />
    );
  }

  // changing to normal render as prop change is not reflected in content decider container's main content

  if (loading || priorityLoading || isEmpty(ticket) || personalisationLoading)
    return (
      <SkeletonLoading
        hideStepNav
        hideTable
        showPlainCardView={false}
        showCardView={false}
        showConversationCard
        showPropertyCard
        showInsighteCard
        noThumbnail
        newLayout
      />
    );
  if (!loading && !isEmpty(ticket)) {
    const fldOptions =
      priorityValues?.getField?.options ||
      priorityValues?.getField?.fieldOptions;

    const priorities = fldOptions?.map((el) => ({
      name: el.value,
      id: el?.id,
      colorCode: el?.details?.colorCode
    }));
    return (
      <TicketDetailsPage
        ticketContext={context}
        ticket={ticket}
        actions={actions}
        tab={props.tab}
        refetchTicket={refetchTicket}
        ref={detailsRef}
        priorityValues={priorities || []}
        flags={flags}
        sendClientEvent={sendClientEvent}
        concurrentViewers={concurrentViewers}
        saveFormStatesToPersonalisation={saveFormStatesToPersonalisation}
        formState={
          formDataFromPersonalisation?.ticketDetailFormState?.formState
        }
        isFormCollapsed={
          formDataFromPersonalisation?.ticketDetailFormState?.isFormCollapsed
        }
        readOnly={!ticket?.client && isMSPProduct}
        getItemsToInvoice={getItemsToInvoice}
        productsToInvoice={productsToInvoice}
        isClassicView={props?.isClassicView}
        isMSPProduct={isMSPProduct}
      />
    );
  }
  return (
    <EmptyContainer notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`} />
  );
}

export default MainContainer;
